/**
 * GrowthBook feature base for the BOOK-168 A/B test. Resolved per brand as
 * `show_final_price_from_search_<lowercased brand>`.
 */
export const FINAL_PRICE_FEATURE = 'show_final_price_from_search';

/**
 * Resolves the brand-scoped GrowthBook key.
 *
 * `resolveFinalPriceVariant` builds the key here, so the hook, the non-React reader and the tab
 * latch all evaluate one byte-identical key. It lowercases the brand, unlike
 * `growthBook.getGrowthBookFeature`: two spellings of one feature would put a single user in both
 * arms, with a final-price card above a base-sorted list.
 *
 * @param {string} [brand] - The whitelabel brand.
 * @returns {string} The GrowthBook feature key for this brand.
 */
export const finalPriceFeatureKey = (brand) =>
  `${FINAL_PRICE_FEATURE}_${String(brand ?? '').toLowerCase()}`;

// The tab latch (design D2). Held in module state so every reader on the page shares it, and
// mirrored to session storage so a reload in the same tab restores it: session storage is scoped
// to the tab, which is exactly the lifetime of one arm. The key embeds the feature key, so each
// brand latches on its own.
const STORAGE_PREFIX = 'final-price-arm-v1:';
const HELD_FINAL = 'final';
const HELD_CONTROL = 'control';
const LATCHED_CONTROL = 'latched-control';
const STORED_ARMS = new Set([HELD_FINAL, HELD_CONTROL, LATCHED_CONTROL]);
const heldArms = new Map();

/**
 * The arm this tab already holds for a key, from memory first, then from session storage.
 *
 * Every storage access is guarded: a browser that blocks or throws on session storage behaves as
 * a fresh page on each load, and memory still holds the arm for the life of the page, so the
 * screen never flips.
 *
 * @param {string} key - The GrowthBook feature key.
 * @returns {string|undefined} The held arm, or undefined when the tab has none.
 */
const readHeldArm = (key) => {
  if (heldArms.has(key)) return heldArms.get(key);

  let stored;
  try {
    stored = window.sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
  } catch {
    return undefined;
  }
  if (!STORED_ARMS.has(stored)) return undefined;

  heldArms.set(key, stored);
  return stored;
};

/**
 * Holds an arm for the rest of the tab.
 *
 * @param {string} key - The GrowthBook feature key.
 * @param {string} arm - One of the stored arm values.
 */
const holdArm = (key, arm) => {
  heldArms.set(key, arm);
  try {
    window.sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, arm);
  } catch {
    // Memory still holds it for this page; a reload then starts as a fresh page.
  }
};

/**
 * Whether the exchange slice holds an exchange in progress, with the predicate the search surfaces
 * use: operation numbers and the ticket's NIT. Both readers fall back to it when a caller passes no
 * `isExchange`, so a caller that omits it cannot let an exchange into the final arm (D10).
 *
 * @param {object} [exchange] - The `exchange` state slice.
 * @returns {boolean} Whether the flow is an exchange.
 */
export const isExchangeInProgress = (exchange) =>
  Boolean(exchange?.operationNumbers?.length && exchange?.ticket?.nit);

/**
 * Steps 1 to 4 of the final-arm rule (design D1): every condition that needs neither GrowthBook
 * nor the latch, cheapest first.
 *
 * @param {object} inputs - The gate inputs.
 * @param {object} [inputs.features] - The whitelabel features.
 * @param {boolean} [inputs.isExchange] - Whether the flow is an exchange.
 * @param {boolean} [inputs.hasDisplayTotal] - Whether the payload carries an INS-1288 display total.
 * @param {boolean} [inputs.hasMarkup] - Whether the rendered pricing has a companion above its total.
 * @returns {boolean} Whether this surface could show a difference between the arms.
 */
const passesFinalPriceGates = ({ features, isExchange, hasDisplayTotal, hasMarkup }) => {
  if (features?.SHOW_FINAL_PRICE_FROM_SEARCH !== true) return false;
  // Exchanges charge no fee, and hiding per-seat prices would break seat-based totals (D10).
  if (isExchange || features.USE_SEAT_BASED_PRICING) return false;
  // INS-1288 takes precedence: its tenants and payloads never see the markup price.
  if (hasDisplayTotal || features.SHOW_TICKET_PRICE_WITHOUT_FEES === true) return false;
  return Boolean(hasMarkup);
};

/**
 * Whether this user sees the markup-inclusive ("final") price, in the order of design D1:
 * 1. `SHOW_FINAL_PRICE_FROM_SEARCH` is `true`;
 * 2. not an exchange, and no seat-based pricing;
 * 3. outside INS-1288: no `displayTotal` and `SHOW_TICKET_PRICE_WITHOUT_FEES` not `true`;
 * 4. the rendered pricing has `totalWithMarkup` above `total`;
 * 5. the tab latch;
 * 6. GrowthBook returns exactly `true` for `show_final_price_from_search_<brand>`.
 *
 * GrowthBook is evaluated last and at most once per tab, so `$experiment_started` fires only for
 * users whose screen would differ, and nobody watches a price change under them:
 * - A tab that already holds an arm restores it without evaluating, including after a reload,
 *   so a reload before GrowthBook loads keeps the arm.
 * - With nothing held and the features not loaded (`ready` false, or no GrowthBook for the
 *   tenant), the tab latches control and never evaluates the key again. CON-1240's
 *   `whenFeaturesReady` is not "loaded": it also resolves on failure and after a 3 s timeout.
 * - Otherwise the key is evaluated once, and the answer is held for the tab.
 *
 * Steps 1 to 4 run before the held arm is read, so the config flags and the API's companions stay
 * an immediate kill switch. A missing GrowthBook `id` needs no special case: the SDK skips an
 * experiment rule without logging an exposure and returns the default (control), while a forced
 * rule still applies -- the graduation path and sandbox QA rely on that. An unset, failed or
 * deleted value is control (KD5).
 *
 * @param {object} inputs - The gate inputs and the GrowthBook instance.
 * @param {object} [inputs.features] - The whitelabel features.
 * @param {string} [inputs.brand] - The whitelabel brand.
 * @param {boolean} [inputs.isExchange] - Whether the flow is an exchange.
 * @param {boolean} [inputs.hasDisplayTotal] - Whether the payload carries an INS-1288 display total.
 * @param {boolean} [inputs.hasMarkup] - Whether the rendered pricing has a companion above its
 *   total (`hasMarkupAboveTotal`).
 * @param {object} [inputs.growthBookInstance] - The GrowthBook SDK instance, if the tenant has one.
 * @returns {boolean} Whether to paint the final price.
 */
export const resolveFinalPriceVariant = ({
  features,
  brand,
  isExchange,
  hasDisplayTotal,
  hasMarkup,
  growthBookInstance,
}) => {
  if (!passesFinalPriceGates({ features, isExchange, hasDisplayTotal, hasMarkup })) return false;
  // Without a brand the key would resolve to `show_final_price_from_search_`, not this tenant's
  // feature. Control until the envs land, without latching the tab.
  if (!brand) return false;

  const key = finalPriceFeatureKey(brand);
  const heldArm = readHeldArm(key);
  if (heldArm) return heldArm === HELD_FINAL;

  if (!growthBookInstance?.ready) {
    holdArm(key, LATCHED_CONTROL);
    return false;
  }

  const isFinal = growthBookInstance.getFeatureValue(key) === true;
  holdArm(key, isFinal ? HELD_FINAL : HELD_CONTROL);
  return isFinal;
};
